<template>
  <div class="doc-instruction">
    <aside class="doc-sidebar">
      <h1>HERON6000 User Manual</h1>
      <nav class="doc-toc">
        <div v-for="(item, index) in menuItems" :key="index" class="menu"
          :class="{ active: currentSection === item.ref }" @click="scrollToSection(item.ref)">
          {{ item.text }}<span v-if="item.important" class="important">&#9733;</span>
        </div>
      </nav>
    </aside>

    <div class="doc-content">

      <section class="doc-section" ref="install">
        <h2>{{ itemIndex('install') }}、Installation</h2>
        <p>
          {{ itemIndex('install') }}.1、Open the installation package, select the language for installation as prompted, then choose the installation path, click Next, and wait for the installation to complete. After completion, you can open the software using the shortcut.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/install-1-en.png')" alt="Installation Interface" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/install-2-en.png')" alt="Installation Interface" />
        </div>
        <p>
          {{ itemIndex('install') }}.2、Open the software installation directory, the HERON6000[127.0.0.1-9009].exe file in the software directory is an auxiliary file to open the software, only used when the industrial control machine is deployed and accessed remotely. Normally, this file is not needed.
          When using, replace 127.0.0.1 with the IP address of the industrial control machine, and 9009 with the port of the industrial control machine, such as HERON6000[192.168.1.100-9011].exe.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/install-3.png')" alt="Installation Interface" />
        </div>
         <p>
          {{ itemIndex('install') }}.3、When uninstalling the software, the interface will prompt whether to keep the data. If you choose to keep the data, after uninstalling and reinstalling the software, select the original installation path, and the previous software data will be retained; if you choose not to keep the data, after uninstalling and reinstalling the software, the previous software data will be cleared.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/install-4-en.png')" alt="Installation Interface" />
        </div>
      </section>

      <section class="doc-section" ref="login">
        <h2>{{ itemIndex('login') }}、Login</h2>
        <p>
          {{ itemIndex('login') }}.1、The login interface will display the current software version and documentation link. Users can log in using the initial account and password: admin/123456. After logging in, please change the password promptly to ensure account security.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/login.png')" alt="Login Interface" />
        </div>
      </section>

      <section class="doc-section" ref="register">
        <h2>{{ itemIndex('register') }}、Activation</h2>
        <p>
          {{ itemIndex('register') }}.1、When adding a device, you need to select the device type. The relevant type needs to fill in the corresponding activation code here to activate. Please send the serial number or QR code to the merchant to obtain the activation code.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/register-1-en.png')" alt="Activation" />
        </div>
      </section>

      <section class="doc-section" ref="machine">
        <h2>{{ itemIndex('machine') }}、Device</h2>
        <p>
          {{ itemIndex('machine') }}.1、New equipment requires equipment type.<span style="color:red">Note: The equipment type needs to be activated to be displayed. The activation function is in the top menu bar.</span>
          The device number and IP cannot be duplicated, and other information should be filled in according to the format.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-2-en.png')" alt="Device" />
        </div>
        <p>
          {{ itemIndex('machine') }}.2、Device operations include adding, editing, deleting, etc. The restore function will only be displayed after deletion. There is a filter function on the right side, which can filter devices by region, status, etc. The controller version supports range search. For example, entering 3.02-3.05 can find all versions from 3.02 to 3.05.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-1-en.png')" alt="Device" />
        </div>
        <p>
          The controller version supports range search.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-9-en.png')" alt="Device" />
        </div>
        <p>
          {{ itemIndex('machine') }}.3、Device import and export functions. Exporting will export the devices under the current filter conditions to an Excel file, and importing will import the Excel file that meets the format into the system to add devices. Importing is also done by region, and if the corresponding region does not exist, the system will automatically create the region.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-3-en.png')" alt="Device" />
        </div>
        <p>
          {{ itemIndex('machine') }}.4、Device upgrade. This function only supports HRC-803 type devices. First, select the device to be upgraded, click upgrade, and a upgrade dialog will pop up. Then select the upgrade package, fill in your account password, and click confirm to upgrade.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-4-en.png')" alt="Device" />
        </div>
        <p>
          After clicking upgrade, the page enters the upgrade monitoring panel, where you can view the upgrade progress and results in real-time. You can cancel the operation midway, but the cancel operation can only cancel devices that are waiting to start. Devices that have already started cannot be canceled. If some devices fail to upgrade, you can retry after finishing.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-5-en.png')" alt="Device" />
        </div>
        <p>
          {{ itemIndex('machine') }}.5、Parameter backup/restore function. Click parameter backup, and a backup dialog will pop up. The dialog has two functions: one is to select the corresponding parameters for backup, and the other is to download the already backed-up files.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-6-en.png')" alt="Device" />
        </div>
        <p>
          Click parameter restore, and a restore dialog will pop up. Select the downloaded backup tar file to upload, fill in your account password, and the corresponding device parameters will be restored.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-7-en.png')" alt="Device" />
        </div>
        <p>
          Parameter backup/restore supports both progress overview and details, and you can also perform cancel and retry operations.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/machine-8-en.png')" alt="Device" />
        </div>
      </section>

      <section class="doc-section" ref="layout">
        <h2>{{ itemIndex('layout') }}、Page Layout</h2>
        <p>
          {{ itemIndex('layout') }}.1、The top of the page is the menu bar, with various function menus on the left and language switch, user information, and logout functions on the right. The left side of the page has a hidden sidebar, and the right side is the main content area. The sidebar can be expanded when the mouse moves to the left edge, and after being displayed, it can be closed by clicking the X in the upper right corner of the sidebar.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/page-en.png')" alt="Page Layout" />
        </div>
      </section>

      <section class="doc-section" ref="dashboard">
        <h2>{{ itemIndex('dashboard') }}、Dashboard</h2>
        <p>
          {{ itemIndex('dashboard') }}.1、Displays the currently added devices by region. The top is the operation area: the left side can display/collapse the device list, the middle shows the online/offline/reconnecting status statistics, and the right side can search for devices through the search box and operate all online/offline.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/dashboard-1-en.png')" alt="Dashboard Overview" />
        </div>
        <p>
          {{ itemIndex('dashboard') }}.2、The device list displays all devices by region. Each device is represented as a card, showing basic information and current status. Click the card's action button to perform the corresponding operation, and click other areas of the card to enter the device monitoring panel.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/dashboard-2-en.png')" alt="Dashboard Device List" />
        </div>
      </section>

      <section class="doc-section" ref="sidebar">
        <h2>{{ itemIndex('sidebar') }}、Left Sidebar</h2>
        <p>
          {{ itemIndex('sidebar') }}.1、When the mouse moves to the left edge of the page, the sidebar automatically expands; similar to the dashboard, it displays the device list by region. Click the X in the upper right corner of the sidebar to close it. Click the corresponding region to expand/collapse devices, and click a device to open its monitoring panel. The circle on the left side of the device indicates its status: green for online, gray for offline, yellow for reconnecting, and red for alarm.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/sidebar-en.png')" alt="Left Sidebar" />
        </div>
      </section>

      <section class="doc-section" ref="region">
        <h2>{{ itemIndex('region') }}、Region</h2>
        <p>
          {{ itemIndex('region') }}.1、Regions can be added, edited, deleted, etc. The restore option will only be displayed after deletion, and regions with existing devices cannot be deleted.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/productline-1-en.png')" alt="Region" />
        </div>
        <p>
          {{ itemIndex('region') }}.2、To add a region, simply fill in the name and order, and optionally provide a description.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/productline-2-en.png')" alt="Region" />
        </div>
      </section>

      <section class="doc-section" ref="user">
        <h2>{{ itemIndex('user') }}、User</h2>
        <p>
          {{ itemIndex('user') }}.1、Users can perform add, edit, delete, and restore functions. The restore option will only be displayed after deletion.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/user-1-en.png')" alt="User" />
        </div>
        <p>
          {{ itemIndex('user') }}.2、When adding/editing a user, you need to fill in the account, name, password, confirm password, role, etc., and optionally provide a description. The role involves the user's permission configuration, with different roles having different permissions. Roles can be edited in the menu later.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/user-2-en.png')" alt="User" />
        </div>
      </section>

      <section class="doc-section" ref="role">
        <h2>{{ itemIndex('role') }}、Role</h2>
        <p>
          {{ itemIndex('role') }}.1、Roles can perform add, edit, delete, and restore functions. The restore option will only be displayed after deletion.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/role-1-en.png')" alt="Role" />
        </div>
        <p>
          {{ itemIndex('role') }}.2、Clicking on the permissions opens the role permissions dialog, which displays all permission lists, generally divided into view and manage, corresponding to the display and functional operations of each page. By checking the corresponding permissions, they are assigned to the role, and combined with the user's role for permission control.
          <span style="color:red">Note: After modifying permissions, the corresponding user needs to log in again for the changes to take effect.</span>
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/role-2-en.png')" alt="Role" />
        </div>
      </section>

      <section class="doc-section" ref="library">
        <h2>{{ itemIndex('library') }}、Parameter Synchronization</h2>
        <p>
          {{ itemIndex('library') }}.1、Parameter synchronization is divided into system parameter synchronization and specification parameter synchronization.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/library-1-en.png')" alt="Parameter Synchronization" />
        </div>
        <p>
          {{ itemIndex('library') }}.2、System parameters. You can select source and target devices, with the source device being selectable only once, and the target device being selectable multiple times. You can perform comparison and download operations. The first column of the comparison results shows the parameter name, the second column displays the parameter from the source device, and the subsequent columns show parameters from the target devices.
          Additionally, you can perform downloads to completely synchronize parameters from the source device to the target device. Please note to back up device parameters when downloading to prevent any issues.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/library-2-en.png')" alt="Parameter Synchronization" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/library-3-en.png')" alt="Parameter Synchronization" />
        </div>
         <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/library-4-en.png')" alt="Parameter Synchronization" />
        </div>       
        <p>
          {{ itemIndex('library') }}.3、Specification parameters. By selecting the specification number ranges of two devices, note that the range quantities must be consistent. After selection, you can perform comparisons, and arrays with differences will be highlighted in red. You can also execute synchronization, but note that the specification numbers will be completely overwritten, so it is recommended to back up first.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/library-5-en.png')" alt="Parameter Synchronization" />
        </div>
      </section>

        <section class="doc-section" ref="expulsion">
        <h2>{{ itemIndex('expulsion') }}、Splash Statistics。</h2>
        <p>
          {{ itemIndex('expulsion') }}.1、Spatter Statistics is a feature used to track spatter during welding. You must first configure the relevant parameters for Spatter Statistics. These parameters can be configured globally or by specification number.
          The global parameters are as follows: In the UIR settings, if the “Use Global Resistance Threshold Parameters” option is set to “Off,” the global configuration will not be used, and the settings for the specification number will take precedence. If it is set to “On,” the two parameters in this tab will be used.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/expulsion-1-en.png')" alt="Splash Statistics" />
        </div>
        <p>
          {{ itemIndex('expulsion') }}.2、For the “Global Selection of Resistance Threshold Parameters” option, when disabled, the two parameters specified in the standard number are used.<br />
          Spatter Detection: The spatter time window threshold value is used as the time interval, and the spatter recognition threshold is used as the rate-of-change threshold. For example, if the spatter time window threshold is set to 3 and the spatter detection threshold is set to 20,
          the system iterates through all welding time points. If the resistance value at 1 ms is A, and the resistance value at 4 ms is B, the drop rate is calculated as (A-B)/A*100. If this value exceeds 20%, the system determines that this time point is a spatter point.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/expulsion-2-en.png')" alt="Splash Statistics" />
        </div>
        <p>
          {{ itemIndex('expulsion') }}.2、Splatter statistics allow you to select a specific time range and calculate the splatter acceptance rate by region, equipment, weld point, specification, vehicle model, chassis VIN, or plant.
          Specifically, after selecting equipment, you can further select a specification number; after selecting a weld point, you can filter by weld point range; after selecting a specification, you can filter by specification number; after selecting a vehicle model, you can further select specific models; and after selecting a chassis VIN, you can enter a VIN range.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/expulsion-3-en.png')" alt="Splash Statistics" />
        </div>
      </section>

      <section class="doc-section" ref="fix">
        <h2>{{ itemIndex('fix') }}、Grinding Monitoring。</h2>
        <p>
          {{ itemIndex('fix') }}.1、Grinding monitoring depends on the grinding configuration. As shown in the figure below, each machine has its own grinding configuration, and the grinding reference curve must be configured based on welding data.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-1-en.png')" alt="Grinding Monitoring" />
        </div>
        <p>
          {{ itemIndex('fix') }}.1、Enter the specification numbers before and after grinding, then click “Search” to view the grinding resistance curves for the past three days. Please note that the grinding records must be continuous before and after the grinding process in order to be retrieved.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-2-en.png')" alt="Grinding Monitoring" />
        </div>
        <p>
          {{ itemIndex('fix') }}.2、Check the resistance curves in the list below; a preview of the selected curves will appear on the right. You can check the “Automatically exclude upper and lower tolerances” option as needed.
          Once checked, the system will calculate the average of the selected post-grinding resistance curves. If any resistance curve falls outside the tolerance range of the average curve, it will be automatically excluded. Click “Generate Reference Curve” to generate a reference curve based on the average of the selected curves.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-3-en.png')" alt="Grinding Monitoring" />
        </div>
         <p>
          {{ itemIndex('fix') }}.4、Configure the alarm parameters. First, enable “Enable Pre-filtering.” If the average resistance of a curve before and after grinding exceeds the specified value, the curve is considered normal, and no tolerance range assessment is performed.
          <br />Second, “Post-grinding curve tolerance”: After configuring the appropriate values, compare the post-grinding curve with the reference curve to determine whether it falls within the tolerance range. Click “Save Reference Curve” for the monitoring settings to take effect.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-4-en.png')" alt="Grinding Monitoring" />
        </div>

         <p>
          {{ itemIndex('fix') }}.5、Grinding Alerts: Currently, there are only two types of alerts. The first is when the resistance value after grinding is higher than the resistance value before grinding. Click to view, and the corresponding pre- and post-grinding curves will be displayed on the right.
          The reference curve, along with the upper and lower tolerance limits shown as dashed lines
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-5-en.png')" alt="Grinding Monitoring" />
        </div>
         <p>
                    The second scenario: “The post-grinding curve exceeds the tolerance range.” Click to view. The dashed lines indicate the upper and lower tolerance limits of the reference curve. Compare the real-time curve with the post-grinding curve.
          If the value falls outside the tolerance range, a large red dot will appear, indicating that the curve is outside the tolerance range after regrinding.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/fix-6-en.png')" alt="Grinding Monitoring" />
        </div>
      
      </section>

      <section class="doc-section" ref="history">
        <h2>{{ itemIndex('history') }}、System Records</h2>
        <p>
          {{ itemIndex('history') }}.1、Records related operations and events on the platform, which can be searched by time and type. This includes device upgrades and device parameter restoration records.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/history-1-en.png')" alt="System Records" />
        </div>
      </section>

      <section class="doc-section" ref="init">
        <h2>{{ itemIndex('init') }}、System Initialization</h2>
        <p>
          {{ itemIndex('init') }}.1、Select the corresponding initialization item to clear the corresponding data table.
          You can choose to clear data every 1 week, 1 month, 3 months, 6 months, or 1 year. If you select 1 week, data older than 1 week will be deleted.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/init-1-en.png')" alt="System Initialization" />
        </div>
      </section>

      <section class="doc-section" ref="running">
        <h2>{{ itemIndex('running') }}、Real-time Monitoring</h2>
        <p>
          {{ itemIndex('running') }}.1、Click on the blank area of the device card to enter the real-time monitoring panel. The top right of the panel has breadcrumb navigation, showing the device online status, and allowing online/offline operations. Open alarm records. <br />
          The device status shows the current working mode of the device, and you can click to switch.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-1-en.png')" alt="Real-time Monitoring" />
        </div>
        <p>
          {{ itemIndex('running') }}.2、Switch the card to IO status to view the input and output status.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-2-en.png')" alt="Input and Output Status" />
        </div>
        <p>
          {{ itemIndex('running') }}.3、Switch the card to Welding Waveform. If the welding machine is working normally, you can view the welding waveform. The curves are divided into four types, and you can switch the curves to be collected below the waveform.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-3-en.png')" alt="Welding Waveform" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-4-en.png')" alt="Welding Waveform" />
        </div>
        <p>
          {{ itemIndex('running') }}.4、Switch the card to Controller Information to view the detailed status and parameters of the controller.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-5-en.png')" alt="Controller Information" />
        </div>
          <p>
          {{ itemIndex('running') }}.5、Switch to the “Trend View” tab to view real-time and historical welding records, which are displayed in chronological order.
          <br />On the left, you can select the corresponding standard number or solder joint mapping number.
          <br />Click a point on the trend chart to view its details, which will appear in the lower-left corner.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/running-6-en.png')" alt="控制器信息" />
        </div>
      </section>

      <section class="doc-section" ref="program">
        <h2>{{ itemIndex('program') }}、Standard Parameters</h2>
        <p>
          {{ itemIndex('program') }}.1、Standard parameters are divided into device-side/offline-side. The parameters on both sides are consistent. Downloading on the device side will synchronize to the device, while saving on the offline side can only save locally. Both sides can synchronize with each other and also implement import and export functions.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/program-1-en.png')" alt="Standard Parameters" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/program-2-en.png')" alt="Standard Parameters" />
        </div>
      </section>

      <section class="doc-section" ref="production">
        <h2>{{ itemIndex('production') }}、Production Management</h2>
        <p>
          {{ itemIndex('production') }}.1、Production management is used to configure some parameters of production management, and also includes functions such as electrode counters and electrode dressing.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/production-1-en.png')" alt="Production Management" />
        </div>
        <p>
          {{ itemIndex('production') }}.2、Electrode counter configuration is the same, and the corresponding count value can be reset. After setting the reset, you need to click download for it to take effect.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/production-2-en.png')" alt="Production Management" />
        </div>
        <p>
          {{ itemIndex('production') }}.3、Electrode dressing, the function is also the same. The step increment setting needs to first enable the step increment function and set the step increment number greater than 0 before performing the dressing operation.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/production-3-en.png')" alt="Production Management" />
        </div>
      </section>

      <section class="doc-section" ref="system">
        <h2>{{ itemIndex('system') }}、System Parameters</h2>
        <p>
          {{ itemIndex('system') }}.1、System basic parameters can be modified as needed. Remember to click download for the changes to synchronize to the device.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-1-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.2、Welding tongs configuration. This option will appear only when selecting robot tongs or suspended welding in the system parameters.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-2-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.3、Electrode calibration, the operation is the same for all 5 electrodes. You can choose electrode pressure/primary current/secondary current. Current verification can be set to default or self-calibration.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-3-en.png')" alt="System Parameters" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-4-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.4、UIR settings. UIR parameters can be filled in as needed, and after filling in, click download to synchronize to the device.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-5-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.5、UIR curve, Collect the waveform data for a period of time, remembering to press “Start Recording UIR.” Once the collection is complete, click “Stop Recording,” and a reference curve will be generated.
          After generation, download it to the controller, and the reference curve will be successfully created. To view the reference curve, observe it in the real-time welding waveform monitor. Please note that to view the reference curve, you must enable the UIR option in the system parameters..
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-6-en.png')" alt="System Parameters" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-11-en.png')" alt="System Parameters" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-12-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.6、UIP settings, related to UIP rectangular graph parameters.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-7-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.7、UIP rectangular graph, can switch specification number.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-8-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.8、Software upgrade, can upgrade the current device. Restore factory settings, currently only clears specification parameters.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-9-en.png')" alt="System Parameters" />
        </div>
        <p>
          {{ itemIndex('system') }}.9、System backup, currently only backs up system parameters.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/system-10-en.png')" alt="System Parameters" />
        </div>
      </section>

      <section class="doc-section" ref="record">
        <h2>{{ itemIndex('record') }}、Welding Records.</h2>
        <p>
          {{ itemIndex('record') }}.1、Query welding records based on time and other parameters. You can open details on the right side of the welding records. If there are alarms during the welding process, there will also be an entry to open the alarms. In the welding details, you can switch tabs to view welding data or waveform, and you can also switch icons to go to the next or previous welding record.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/record-1-en.png')" alt="Welding Records" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/record-2-en.png')" alt="Welding Records" />
        </div>
      </section>

      <section class="doc-section" ref="alarm">
        <h2>{{ itemIndex('alarm') }}、Alarm Records.</h2>
        <p>
          {{ itemIndex('alarm') }}.1、Find alarm records within a certain time range. If the alarm occurred during a welding record, you can click to view and jump to the welding record page.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/alarm-1-en.png')" alt="Alarm Records" />
        </div>
      </section>

      <section class="doc-section" ref="modification">
        <h2>{{ itemIndex('modification') }}、Modification Records.</h2>
        <p>
          {{ itemIndex('modification') }}.1、Records generated when users modify system parameters or specification parameters. Card welding management records are generated when users adjust welding points.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/modification-1-en.png')" alt="Modification Records" />
        </div>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/modification-2-en.png')" alt="Modification Records" />
        </div>
      </section>

      <section class="doc-section" ref="prodspot">
        <h2>{{ itemIndex('prodspot') }}、Welding Point Management.</h2>
        <p>
          {{ itemIndex('prodspot') }}.1、You can perform related operations.
          <br />Refresh: Completely clear the offline database data and pull the welding point data from the device again.
          <br />Import: Import Excel files that meet the format into the system, merge with the offline database, and distribute the merged data to the device.
          <br />Verify: Check if the welding point verification position is correct.
          <br />Export: Export the offline database data to an Excel file.
          <br />Clear: Clear the offline database data and device data.
          <br />New Welding Point: Manually add new welding point data. You can edit the welding point data for each welding record operation. Remember to perform the download operation after editing to synchronize with the device.
          <br />Download: Download the current offline database data to the device.
        </p>
        <div class="doc-image-wrap">
          <img :src="require('@/assets/img/instruction/prodspot-1-en.png')" alt="Welding Point Management" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DocInstruction',
  components: {},
  props: {},
  data() {
    return {
      currentSection: 'install',
      menuItems: [
        { ref: 'install', text: '1、Installation' },
        { ref: 'login', text: '2、Login' },        
        { ref: 'register', text: '3、Activation', important: true },
        { ref: 'machine', text: '4、Device', important: true },
        { ref: 'layout', text: '5、Page Layout' },
        { ref: 'dashboard', text: '6、Dashboard' },
        { ref: 'sidebar', text: '7、Left Sidebar' },
        { ref: 'region', text: '8、Region' },
        { ref: 'user', text: '9、User' },
        { ref: 'role', text: '10、Role' },
        { ref: 'library', text: '11、Parameter Synchronization' },
        { ref: 'expulsion', text: '12、Splash Statistics' },
        { ref: 'fix', text: '13、Grinding Monitoring' },
        { ref: 'history', text: '14、System Records' },
        { ref: 'init', text: '15、System Initialization' },
        { ref: 'running', text: '16、Real-time Monitoring' },
        { ref: 'program', text: '17、Specification Parameters' },
        { ref: 'production', text: '18、Production Management' },
        { ref: 'system', text: '19、System Parameters' },
        { ref: 'record', text: '20、Welding Records' },
        { ref: 'alarm', text: '21、Alarm Records' },
        { ref: 'modification', text: '22、Modification Records' },
        { ref: 'prodspot', text: '23、Welding Point Management' },
       
      ]
    }
  },
  computed: {},
  watch: {},
  created() { },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
  },
  methods: {
    scrollToSection(refName) {
      this.currentSection = refName;
      const element = this.$refs[refName];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' }); // 关键API
      }
    },
    handleScroll() {
      const scrollPosition = window.scrollY || document.documentElement.scrollTop;

      // 遍历所有ref，找到当前应该高亮的section
      for (let refName in this.$refs) {
        const element = this.$refs[refName];
        if (element && element.offsetTop <= scrollPosition + 100) {
          this.currentSection = refName;
        }
      }
    },
    itemIndex(refName) {
      let rs = 0;
      this.menuItems.forEach((item, index) => {
        if (item.ref === refName) {
          rs= index;
        }
      });
      return rs+1;
    }
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style scoped>
.doc-instruction {
  min-height: 60vh;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 24px;
  align-items: start;
  padding: 24px;
  background: #f8fafc;
  scroll-behavior: smooth;
  /* smooth anchor scroll */
}

.doc-sidebar {
  position: sticky;
  top: 16px;
  align-self: start;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.doc-sidebar h1 {
  margin: 0 0 12px;
  font-size: 18px;
  color: #0f172a;
}

.doc-toc {
  display: grid;
  gap: 8px;
}

.doc-toc a {
  display: block;
  color: #334155;
  font-size: 14px;
  text-decoration: none;
  padding: 8px 10px;
  border-radius: 8px;
  transition: color .15s ease, background .15s ease;
}

.doc-toc a:hover {
  color: #2563eb;
  background: #f1f5f9;
}

.doc-content {
  width: 100%;
  max-width: 1024px;
  text-align: left;
  color: #0f172a;
}

.doc-section+.doc-section {
  margin-top: 24px;
}

h2 {
  margin: 0 0 12px;
  font-size: 22px;
  line-height: 1.4;
  font-weight: 700;
  color: #0f172a;
}

p {
  margin: 12px 0 0;
  font-size: 16px;
  line-height: 1.7;
  color: #334155;
}

.doc-image-wrap {
  display: flex;
  justify-content: center;
  /* image centered on its own line */
  align-items: center;
  margin: 8px 0;
}

.doc-image-wrap img {
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: 55vh;
  /* avoid exceeding one screen */
  object-fit: contain;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.menu {
  /* 原有样式 */
  padding: 0px 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.menu.active {
  background-color: #1890ff;
  /* 激活背景色 */
  color: white;
  /* 激活文字颜色 */
  font-weight: bold;
  border-radius: 4px;
}

/* 或者使用更明显的效果 */
.menu.active {
  background-color: rgba(24, 144, 255, 0.1);
  color: #1890ff;
  border-left: 3px solid #1890ff;
  padding-left: 9px;
  /* 3px边框补偿 */
}

/* 鼠标悬停效果 */
.menu:hover:not(.active) {
  background-color: #f5f5f5;
  color: #333;
}

.important {
  color: red;
}
</style>